import {
  FileText,
  Layout,
  BarChart3,
  Target,
  TrendingUp,
  Zap,
  Users,
  Activity,
} from 'lucide-react';
import KeywordLandingPage, {
  type ProblemCard,
  type FeatureCard,
} from '../components/KeywordLandingPage';

const problemCards: ProblemCard[] = [
  {
    title: 'The direction is hard to pin down',
    description:
      'Ideas about growth are easy to discuss. Agreeing on a vision, priorities, and measurable objectives takes a shared structure.',
    icon: FileText,
    iconBgClass: 'bg-red-100',
    iconColorClass: 'text-red-600',
  },
  {
    title: 'Useful analysis stays separate',
    description:
      'Market assessments and internal reviews lose value when their findings are difficult to bring into strategic decisions.',
    icon: Layout,
    iconBgClass: 'bg-orange-100',
    iconColorClass: 'text-orange-600',
  },
  {
    title: 'The plan stops before the next step',
    description:
      'A goal needs a strategy, an owner, and specific work before the team can move it forward.',
    icon: BarChart3,
    iconBgClass: 'bg-yellow-100',
    iconColorClass: 'text-yellow-600',
  },
];

const features: FeatureCard[] = [
  {
    title: 'A clear foundation',
    description: 'Guided builders help you articulate your vision, mission, and values.',
    icon: Target,
    gradientFrom: 'from-blue-50',
    gradientTo: 'to-blue-100',
    iconColorClass: 'text-blue-600',
  },
  {
    title: 'Structured business assessments',
    description: 'Save PESTEL, Five Forces, SWOT, driving forces, and success factors.',
    icon: Layout,
    gradientFrom: 'from-purple-50',
    gradientTo: 'to-purple-100',
    iconColorClass: 'text-purple-600',
  },
  {
    title: 'Measurable objectives',
    description:
      'Define what success looks like, how you will measure it, and when you aim to achieve it.',
    icon: TrendingUp,
    gradientFrom: 'from-green-50',
    gradientTo: 'to-green-100',
    iconColorClass: 'text-green-600',
  },
  {
    title: 'AI starting points',
    description:
      'Draft foundation statements and strategies using the company context you provide.',
    icon: Zap,
    gradientFrom: 'from-orange-50',
    gradientTo: 'to-orange-100',
    iconColorClass: 'text-orange-600',
  },
  {
    title: 'Visible ownership',
    description: 'Map your organisation and assign responsibility for projects and tasks.',
    icon: Users,
    gradientFrom: 'from-red-50',
    gradientTo: 'to-red-100',
    iconColorClass: 'text-red-600',
  },
  {
    title: 'A plan you can present',
    description: 'Bring your strategy into custom visual reports and export a PDF.',
    icon: Activity,
    gradientFrom: 'from-indigo-50',
    gradientTo: 'to-indigo-100',
    iconColorClass: 'text-indigo-600',
  },
];

export default function StrategicPlanningSoftwarePage() {
  return (
    <KeywordLandingPage
      headline="Give your company's next move a clear plan."
      subheadline="Turn your vision, business analysis, and priorities into a strategic plan connected to the projects and tasks that will deliver it."
      bullets={[
        'Define your vision, mission, and values',
        'Work through PESTEL, Five Forces, and SWOT',
        'Set measurable objectives',
        'Connect strategies to projects and tasks',
      ]}
      badgeText="Strategic Planning"
      showSecondaryCta
      secondaryCtaLabel="Book Strategy Demo"
      problemTitle="A clear direction needs a practical path forward."
      problemSubtitle="Planning gets harder when the big decisions, business analysis, and next steps sit in separate documents."
      problemCards={problemCards}
      solutionTitle="Build your plan from direction to delivery."
      solutionSubtitle="StratPlan guides you through Foundation, External Assessment, Internal Assessment, Strategy Formulation, and the Execution Hub."
      solutionSteps={[
        {
          title: 'Define what you are building',
          description:
            'Shape your vision, mission, and values. Set objectives with measures and target dates to make your direction concrete.',
        },
        {
          title: 'Make informed strategic choices',
          description:
            'Save your external and internal assessments, explore strategy options, and choose priorities for your business.',
        },
        {
          title: 'Give the plan a next step',
          description:
            'Connect strategies to projects and tasks, assign owners, and follow progress in the same workspace.',
        },
      ]}
      featuresTitle="The building blocks of a usable strategic plan"
      features={features}
      finalCtaTitle="Bring your next move into focus."
      finalCtaSubtitle="Create your account and start turning your company direction into a clear strategic plan."
      finalCtaButtonText="Start my strategic plan"
      analyticsCategory="strategic_planning_landing"
      breadcrumbLabel="Strategic Planning"
    />
  );
}
